// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { Modal } from 'components/atoms/Modal';
import { useTranslation } from 'react-i18next';
import styles from './UserFormModal.module.scss';
import { Form } from 'react-final-form';
import { InputDataListField } from 'components/molecules/InputDataListField';
import {
  validateEmailAddress,
  validateId,
  validatePasswordField,
  validateRequiredField,
  validateSelectField,
} from 'util/validator';
import { Account } from 'util/accountManagementTypes';
import { Label } from 'components/atoms/Label';
import Toggle from 'components/atoms/Toggle';
import Popover from 'components/atoms/Popover';
import { IconInfo } from 'components/atoms/icons/IconInfo';
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import { useEffect, useMemo, useState } from 'react';
import { getSites } from 'apis/sites';
import { RoleType } from 'util/Enums';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';

export type UserInput = Pick<
  Account,
  'id' | 'name' | 'type' | 'siteId' | 'email' | 'mfaEnable'
> & {
  password?: string;
};

export const renderMFATooltip = (t: typeof useTranslation) => {
  return (
    <>
      <p className={styles.mfa_tooltip_title}>
        {t('user_security.mfa_tooltip_title')}
      </p>
      <p>{t('user_security.mfa_tooltip_content')}</p>
    </>
  );
};

interface Props {
  onClose: () => void;
  userData: Account;
  onSubmit: (form: UserInput, userId: number | string) => void;
  creatable?: boolean;
}

const UserFormModal: React.FC<Props> = ({
  onClose,
  userData,
  onSubmit,
  creatable,
}) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);

  const isEditing = useMemo(() => !!userData.id, [userData]);
  const isEditingCurrentUser = useMemo(
    () => isEditing && userData.id.toString() === user.userId.toString(),
    [userData, user, isEditing]
  );

  const [siteOptions, setSiteOptions] = useState<SelectOption[]>([]);

  const roleOptions = [
    {
      value: RoleType.AdminOperator,
      label: t('user_form.role_options.admin_operator'),
    },
    {
      value: RoleType.UserNormal,
      label: t('user_form.role_options.user_normal'),
    },
  ];

  useEffect(() => {
    if (!creatable) {
      return;
    }

    getSites({
      successCallback: (data) => {
        setSiteOptions(
          data.sites.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
      },
    });
  }, [creatable]);

  return (
    <div className={styles.userModal}>
      <Modal>
        <div className={styles.modalContent}>
          <div className={styles.customTitle}>
            <p className={styles.descriptionTitle}>
              {t(`user_form.title_${isEditing ? 'edit' : 'add'}`)}
            </p>
            <div className={styles.top} onClick={onClose}>
              <IconCloseTablet />
            </div>
          </div>

          <Form<UserInput>
            initialValues={{
              type: userData.type || '', //role
              siteId: userData.siteId || '',
              name: userData.name || '',
              id: userData.id || '',
              mfaEnable: userData.mfaEnable || false,
              email: userData.email || '',
            }}
            onSubmit={(values) => {
              onSubmit(values, userData.id);
            }}
            mutators={{
              setValue: ([field, value], state, { changeValue }) => {
                changeValue(state, field, () => value);
              },
            }}
            validate={(values) => {
              return {
                ...(creatable && {
                  type: validateSelectField(
                    t('user_form.label_role'),
                    values.type
                  ),
                  siteId:
                    values.type === RoleType.UserNormal
                      ? validateSelectField(
                          t('user_form.label_site_id'),
                          values.siteId
                        )
                      : undefined,
                  name: validateRequiredField(
                    values.name,
                    t('user_form.label_name') as string
                  ),
                  id: validateId(t('login.user_id'), 20, values.id),
                }),
                password:
                  !isEditing || values.password?.length
                    ? validatePasswordField(
                        t('login.password'),
                        6,
                        values.password
                      )
                    : undefined,
                email: values.mfaEnable
                  ? validateEmailAddress(values.email || '', {})
                  : undefined,
              };
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit} className={styles.formInput}>
                <div className={styles.inputFrame}>
                  {creatable && (
                    <>
                      <SelectField
                        label={t('user_form.label_role')}
                        name="type"
                        placeholder={t('common.select') || '選択する'}
                        options={roleOptions}
                        isDarkmode
                        required
                        disabled={isEditingCurrentUser}
                      />

                      {props.values.type === RoleType.UserNormal && (
                        <SelectField
                          label={t('user_form.label_site_id')}
                          name="siteId"
                          placeholder={t('common.select') || '選択する'}
                          options={siteOptions}
                          isDarkmode
                          required
                        />
                      )}

                      <InputDataListField
                        label={t('user_form.label_name') || ''}
                        placeholder={t('user_form.label_name') || ''}
                        name="name"
                        type="text"
                        maxlength={100}
                        required
                        isDarkMode
                      />

                      <InputDataListField
                        label={t('user_form.label_user_id') || ''}
                        placeholder={t('user_form.label_user_id') || ''}
                        name="id"
                        type="text"
                        maxlength={20}
                        required
                        disabled={isEditing}
                        isDarkMode
                      />
                    </>
                  )}

                  <InputDataListField
                    label={t('user_form.label_password') || ''}
                    placeholder={t('user_form.label_password') || ''}
                    name="password"
                    type="password"
                    maxlength={20}
                    required={creatable && !isEditing}
                    isDarkMode
                  />

                  <div className={styles.mfaOnOff}>
                    <div className={styles.mfaOnOffLabel}>
                      <Label
                        className={styles.label}
                        text={t('user_form.label_mfa') || ''}
                        tag={'div'}
                      />

                      <Popover
                        content={renderMFATooltip(t)}
                        arrowContainerConfigs={{
                          arrowColor: '#4d4d4d',
                        }}
                        popUpStyle={{
                          background: '#4d4d4d',
                          maxWidth: '500px',
                          whiteSpace: 'pre-line',
                        }}
                        containerStyle={{
                          zIndex: '100000',
                        }}
                      >
                        <span>
                          <IconInfo />
                        </span>
                      </Popover>
                    </div>

                    <Toggle
                      value={props.values.mfaEnable}
                      setValue={(value) =>
                        props.form.mutators.setValue('mfaEnable', value)
                      }
                      isDarkMode
                    />
                  </div>

                  {props.values.mfaEnable && (
                    <InputDataListField
                      label={t('user_form.label_email') || ''}
                      placeholder={t('user_form.label_email') || ''}
                      name="email"
                      required
                      maxlength={100}
                      isDarkMode
                    />
                  )}
                </div>

                <div className={styles.submit}>
                  <button
                    disabled={props.invalid}
                    className={styles.submitButton}
                    type="submit"
                  >
                    {t(`user_form.button_${userData.id ? 'edit' : 'add'}`)}
                  </button>
                </div>
              </form>
            )}
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default UserFormModal;
